type Gesture = {
    id: number;
    source: "mouse" | "touch";
    x: number;
    y: number;
    delta: number;
    axis?: "horizontal" | "vertical";
};

export class TourTrayDrag {
    private gesture?: Gesture;
    private suppressClick = false;

    constructor(private readonly root: HTMLElement, private readonly callbacks: {
        start: () => void;
        move: (delta: number) => void;
        end: (delta: number | undefined) => void;
    }) {}

    connect(): void {
        this.root.addEventListener("pointerdown", this.onPointerDown);
        this.root.addEventListener("pointermove", this.onPointerMove, true);
        this.root.addEventListener("pointerup", this.onPointerUp);
        this.root.addEventListener("pointercancel", this.cancel);
        this.root.addEventListener("lostpointercapture", this.onLostCapture);
        this.root.addEventListener("touchstart", this.onTouchStart, { passive: true });
        this.root.addEventListener("touchmove", this.onTouchMove, { passive: false });
        this.root.addEventListener("touchend", this.onTouchEnd);
        this.root.addEventListener("touchcancel", this.cancel);
        this.root.addEventListener("click", this.onClick, true);
        window.addEventListener("blur", this.cancel);
    }

    disconnect(): void {
        this.cancel();
        this.root.removeEventListener("pointerdown", this.onPointerDown);
        this.root.removeEventListener("pointermove", this.onPointerMove, true);
        this.root.removeEventListener("pointerup", this.onPointerUp);
        this.root.removeEventListener("pointercancel", this.cancel);
        this.root.removeEventListener("lostpointercapture", this.onLostCapture);
        this.root.removeEventListener("touchstart", this.onTouchStart);
        this.root.removeEventListener("touchmove", this.onTouchMove);
        this.root.removeEventListener("touchend", this.onTouchEnd);
        this.root.removeEventListener("touchcancel", this.cancel);
        this.root.removeEventListener("click", this.onClick, true);
        window.removeEventListener("blur", this.cancel);
    }

    readonly cancel = (): void => this.finish(true);

    private readonly onPointerDown = (event: PointerEvent): void => {
        if (event.pointerType !== "mouse" || event.button !== 0 || !event.isPrimary) return;
        this.begin(event.pointerId, "mouse", event.clientX, event.clientY);
    };

    private readonly onPointerMove = (event: PointerEvent): void => {
        if (this.gesture?.source !== "mouse" || event.pointerId !== this.gesture.id) return;
        if ((event.buttons & 1) === 0) { this.cancel(); return; }
        if (this.move(event.clientX, event.clientY)) {
            this.root.setPointerCapture?.(event.pointerId);
            event.preventDefault();
            // The shared horizontal rail must not acquire a vertical gesture later.
            event.stopImmediatePropagation();
        }
    };

    private readonly onPointerUp = (event: PointerEvent): void => {
        if (this.gesture?.source === "mouse" && event.pointerId === this.gesture.id) this.finish(false);
    };

    private readonly onLostCapture = (event: PointerEvent): void => {
        // Horizontal rails take capture once they own the gesture.
        if (event.target === this.root && this.gesture?.axis !== "horizontal") this.cancel();
    };

    private readonly onTouchStart = (event: TouchEvent): void => {
        if (event.touches.length !== 1) { this.cancel(); return; }
        const touch = event.touches[0];
        this.begin(touch.identifier, "touch", touch.clientX, touch.clientY);
    };

    private readonly onTouchMove = (event: TouchEvent): void => {
        if (this.gesture?.source !== "touch") return;
        if (event.touches.length !== 1 || !event.cancelable) { this.cancel(); return; }
        const touch = event.touches[0];
        if (touch.identifier === this.gesture.id && this.move(touch.clientX, touch.clientY)) event.preventDefault();
    };

    private readonly onTouchEnd = (event: TouchEvent): void => {
        if (this.gesture?.source !== "touch") return;
        const touch = Array.from(event.changedTouches).find(touch => touch.identifier === this.gesture!.id);
        if (!touch) return;
        // Browsers may omit move events for a short gesture and otherwise synthesize a tap.
        if (this.move(touch.clientX, touch.clientY) && event.cancelable) event.preventDefault();
        this.finish(false);
    };

    private begin(id: number, source: Gesture["source"], x: number, y: number): void {
        this.cancel();
        this.suppressClick = false;
        this.gesture = { id, source, x, y, delta: 0 };
    }

    private move(x: number, y: number): boolean {
        const gesture = this.gesture!;
        const dx = x - gesture.x, dy = y - gesture.y;
        if (!gesture.axis) {
            if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return false;
            gesture.axis = Math.abs(dy) > Math.abs(dx) ? "vertical" : "horizontal";
            if (gesture.axis === "vertical") {
                this.suppressClick = true;
                this.callbacks.start();
            }
        }
        if (gesture.axis !== "vertical") return false;
        gesture.delta = dy;
        this.callbacks.move(dy);
        return true;
    }

    private finish(cancelled: boolean): void {
        const gesture = this.gesture;
        this.gesture = undefined;
        if (!gesture) return;
        if (gesture.source === "mouse" && this.root.hasPointerCapture?.(gesture.id)) this.root.releasePointerCapture(gesture.id);
        if (gesture.axis === "vertical") this.callbacks.end(cancelled ? undefined : gesture.delta);
    }

    private readonly onClick = (event: MouseEvent): void => {
        if (!this.suppressClick || event.detail === 0) return;
        this.suppressClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
    };
}
